import { formatAddressSummary } from "@shared/address";
import type { ExportContact } from "@shared/contacts-export";
import { HOME_BOARD } from "./planBoard";
import { sortByName, type SortWay } from "./SortToggle";

/**
 * s237 (ACROSS-YOUR-PLAN-1 PR C) — CONTACTS, the planner's view: PR A's entries (`GET …/contacts-page`), filtered,
 * searched and sorted the way board 12 draws them. Pure.
 */
export type RoleGroup = "family" | "helpers" | "pros" | "biz" | null;
export interface ContactRole { words: string; target: { chapter: number; screen: string } | null; group: RoleGroup }
export interface ContactEntry {
  id: string | null;
  kind: "person" | "organisation";
  name: string;
  firstName: string | null;
  lastName: string | null;
  /** The company this person is the person to ask for at (D-237.69-2), or null. */
  firm?: string | null;
  /** s237 PR F (item 146(1)): a company's people to ask for, by name; [] or absent for a person. */
  askFor?: string[];
  phone: string | null;
  email: string | null;
  address: { street: string | null; street2: string | null; city: string | null; state: string | null; postal: string | null; country: string | null } | null;
  roles: ContactRole[];
}

/** Board 12's chip ids. */
export type ChipId = "all" | "family" | "helpers" | "pros" | "biz" | "companies";

/** Who a chip holds (board 12 `inKind`): Companies by the stored person-or-company flag; the rest by the person's roles. */
export function inChip(c: ContactEntry, chip: ChipId): boolean {
  if (chip === "all") return true;
  if (chip === "companies") return c.kind === "organisation";
  return c.roles.some((r) => r.group === chip);
}

/** The chips to draw: Everyone always, every other chip only when someone is in it, with its count. */
export function chipsFor(entries: readonly ContactEntry[]): { id: ChipId; label: string; count: number }[] {
  return HOME_BOARD.contacts.chips
    .map((c) => ({ id: c.id as ChipId, label: c.label, count: entries.filter((e) => inChip(e, c.id as ChipId)).length }))
    .filter((c) => c.id === "all" || c.count > 0);
}

/** The rows the page shows: the chip, the search (name, a role's words, email, phone), the sort. */
export function shownContacts(entries: readonly ContactEntry[], opts: { chip: ChipId; query: string; sort: SortWay }): ContactEntry[] {
  const q = opts.query.trim().toLowerCase();
  const kept = entries.filter((e) => inChip(e, opts.chip)).filter((e) => !q
    || [e.name, e.email ?? "", e.phone ?? "", ...e.roles.map((r) => r.words)].join(" ").toLowerCase().includes(q));
  return sortByName(kept, (e) => e.name, opts.sort);
}

/** The card's note names a person by their first name, a company by its whole name (board 12 `cardNote`). */
export function cardNoteName(c: ContactEntry): string {
  return c.kind === "organisation" ? c.name : (c.firstName?.trim() || c.name.split(" ")[0]);
}

/** The address as the cell the contact card holds (AddressData JSON), or "" when there is none. */
export function addressCell(c: ContactEntry): string {
  const a = c.address;
  if (!a) return "";
  return JSON.stringify({ street: a.street ?? "", street2: a.street2 ?? "", city: a.city ?? "", state: a.state ?? "", postal: a.postal ?? "", country: a.country ?? "" });
}

/** The address as one line, through the ONE formatter (F-5: never raw address JSON). */
export function addressLine(c: ContactEntry): string {
  return formatAddressSummary(addressCell(c));
}

/**
 * s237 PR F (D-237.72) — who Download and "Contacts to my phone" take: the ticked people, in the page's order (a ticked
 * person stays ticked when a filter hides them), or everyone shown when nobody is ticked. Only people with a record id
 * can be ticked or sent. Pure.
 */
export function downloadScope(entries: readonly ContactEntry[], shown: readonly ContactEntry[], ticked: Readonly<Record<string, boolean>>, sort: SortWay): { list: ContactEntry[]; ticked: boolean } {
  const tickedList = sortByName(entries.filter((e) => !!e.id && ticked[e.id]), (e) => e.name, sort);
  return tickedList.length > 0 ? { list: tickedList, ticked: true } : { list: shown.filter((e) => !!e.id), ticked: false };
}

/** A Contacts entry as the export files read it (shared/contacts-export.ts). Pure. */
export function toExport(c: ContactEntry): ExportContact {
  return { kind: c.kind, name: c.name, firstName: c.firstName, lastName: c.lastName, firm: c.firm ?? null, phone: c.phone, email: c.email, address: c.address, roles: c.roles };
}

/** Save text the page built as a file in the browser's downloads. */
export function saveTextFile(text: string, fileName: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
